// src/components/SearchResults.js

import React, { useContext, useState } from 'react';
import { Card, Row, Col, Button, Carousel } from 'react-bootstrap';
import axios from 'axios';
import { LanguageContext } from '../contexts/LanguageContext';
import { SettingsContext } from '../contexts/SettingsContext';
import LoadingSpinner from './LoadingSpinner'; // Import the LoadingSpinner
import { Clipboard, ClipboardCheck } from 'react-bootstrap-icons'; // ADDED both icons
import Neon3DButton from './Neon3DButton'; // NEW: Import the 3DButton component
import AlertDialog from './AlertDialog';
import useAlertDialog from '../hooks/useAlertDialog';
import './SearchResults.css'; // Import the extracted CSS styles
import { Link } from 'react-router-dom'; // <--- ADDED to allow routing

// Define PrevIcon and NextIcon for Carousel
const PrevIcon = (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="30"
    height="30"
    fill="black"
    className="bi bi-chevron-left carousel-icon"
    viewBox="0 0 16 16"
  >
    <path
      fillRule="evenodd"
      d="M11.354 1.646a.5.5 0 0 1 0 .708L5.707 8l5.647 5.646a.5.5 0 0 1-.708.708l-6-6a.5.5 0 0 1 0-.708l6-6a.5.5 0 0 1 .708 0z"
    />
  </svg>
);

const NextIcon = (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="30"
    height="30"
    fill="black"
    className="bi bi-chevron-right carousel-icon"
    viewBox="0 0 16 16"
  >
    <path
      fillRule="evenodd"
      d="M4.646 1.646a.5.5 0 0 1 .708 0l6 6a.5.5 0 0 1 0 .708l-6 6a.5.5 0 0 1-.708-.708L10.293 8 4.646 2.354a.5.5 0 0 1 0-.708z"
    />
  </svg>
);

/**
 * CopyBarcode Component
 * A button that copies the provided barcode to the clipboard.
 * On click, it shows a tick animation (ClipboardCheck) for 1 seconds.
 */
const CopyBarcode = ({ barcode }) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    navigator.clipboard.writeText(barcode).then(() => {
      setCopied(true);
      // Reset after 1 seconds
      setTimeout(() => setCopied(false), 1000);
    });
  };

  return (
    <Button
      variant="link"
      onClick={handleCopy}
      className={`copy-barcode-btn ${copied ? 'copied' : ''}`}
      title="Copy barcode"
    >
      {copied ? <ClipboardCheck color="#28a745" size={20} /> : <Clipboard size={20} />}
    </Button>
  );
};

/**
 * SearchResults Component
 * Displays the list of products based on search results.
 */
function SearchResults({ products, onUpload }) {
  const { language, translations } = useContext(LanguageContext);
  const { settings } = useContext(SettingsContext);
  const [loading, setLoading] = useState(false); // State to manage loading spinner
  
  // Alert dialog hook
  const { dialogState: alertState, showAlert, hideAlert } = useAlertDialog();

  // Apply settings to limit results
  let filteredProducts = [];

  if (settings.mode === 'default') {
    // Limit all products to 'defaultResults'
    filteredProducts = products.slice(0, settings.defaultResults);
  } else if (settings.mode === 'custom') {
    // Custom mode: limit by category
    const furniture = products
      .filter((p) => {
        const category = (p.category_en || p.category_ka || '').toLowerCase();
        return category.includes('furniture');
      })
      .slice(0, settings.furnitureResults);

    const light = products
      .filter((p) => {
        const category = (p.category_en || p.category_ka || '').toLowerCase();
        return category.includes('light');
      })
      .slice(0, settings.lightResults);

    const accessories = products
      .filter((p) => {
        const category = (p.category_en || p.category_ka || '').toLowerCase();
        return category.includes('accessories');
      })
      .slice(0, settings.accessoriesResults);

    // Combine them back
    filteredProducts = [...furniture, ...light, ...accessories];
  } else {
    // If mode is unrecognized, default to showing 'defaultResults'
    filteredProducts = products.slice(0, settings.defaultResults);
  }

  // NEW: Filter out hidden products so that only unhidden products are shown
  const visibleProducts = filteredProducts.filter((product) => !product.hidden);

  if (visibleProducts.length === 0) {
    return <p>{translations.noProductsFound}</p>;
  }

  /**
   * Handles the "Search with this picture" action.
   * Shows a LoadingSpinner while the request is in progress.
   */
  const handleSearchWithPicture = async (imageUrl) => {
    setLoading(true);
    try {
      // Fetch the image as a blob
      const response = await fetch(imageUrl, { mode: 'cors' });
      if (!response.ok) {
        throw new Error(`Failed to fetch image. Status: ${response.status}`);
      }
      const blob = await response.blob();

      // Convert blob to File
      const file = new File([blob], 'search_image.jpg', { type: blob.type });

      // Create FormData
      const formData = new FormData();
      formData.append('image', file);

      // Send POST request to /api/upload/
      const uploadResponse = await axios.post('/api/upload/', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      // Condition: only show alert in dev, not in production
      if (process.env.NODE_ENV !== 'production') {
        showAlert({
          title: translations.success || 'Success',
          message: (uploadResponse.data.message && uploadResponse.data.message.trim()) || 'Image processed successfully.',
          variant: 'success',
          autoClose: true,
          autoCloseDelay: 2000,
        });
      }

      if (uploadResponse.data.products) {
        // Optional: ensure new results render from the top immediately
        const newProducts = uploadResponse.data.products.slice();
        // Scroll container/page to top before updating products to avoid scroll animation flicker
        if (typeof window !== 'undefined') {
          window.scrollTo({ top: 0, behavior: 'auto' });
        }
        onUpload(newProducts);
      } else {
        if (typeof window !== 'undefined') {
          window.scrollTo({ top: 0, behavior: 'auto' });
        }
        onUpload([]);
      }

      // ---- NEW CODE START: Save search history with uploaded image ----
      const newHistoryItem = {
        id: Date.now(),
        timestamp: new Date().toISOString(),
        products: uploadResponse.data.products,
        uploadedImage: null,
      };
      const reader = new FileReader();
      reader.onloadend = () => {
        newHistoryItem.uploadedImage = reader.result;
        try {
          const stored = JSON.parse(localStorage.getItem('searchHistory') || '[]');
          stored.push(newHistoryItem);
          localStorage.setItem('searchHistory', JSON.stringify(stored));
        } catch (err) {
          if (err.name === 'QuotaExceededError' || err.name === 'NS_ERROR_DOM_QUOTA_REACHED') {
            try {
              let stored = JSON.parse(localStorage.getItem('searchHistory') || '[]');
              // Remove oldest entries until there is enough room
              while (stored.length > 0) {
                stored.shift();
                try {
                  localStorage.setItem('searchHistory', JSON.stringify(stored));
                  // Try saving the new entry again
                  stored.push(newHistoryItem);
                  localStorage.setItem('searchHistory', JSON.stringify(stored));
                  break;
                } catch (e) {
                  if (stored.length === 0) {
                    console.error("LocalStorage quota exceeded even after clearing history.");
                    break;
                  }
                }
              }
            } catch (e) {
              console.error("Error handling quota exceeded:", e);
            }
          } else {
            console.error("Error saving search history to localStorage:", err);
          }
        }
      };
      reader.readAsDataURL(blob);
      // ---- NEW CODE END ----

    } catch (error) {
      console.error('Error searching with picture:', error);
      if (error.response && error.response.data) {
        showAlert({
          title: translations.error || 'Error',
          message: `Error: ${JSON.stringify(error.response.data)}`,
          variant: 'danger',
        });
      } else {
        showAlert({
          title: translations.error || 'Error',
          message: translations.errorSearchingPicture || 'An error occurred while searching with the picture.',
          variant: 'danger',
        });
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Row>
        {loading && <LoadingSpinner />}
        {visibleProducts.map((product) => (
        <Col key={product.id} xs={12} md={6} lg={4} className="mb-4 search-result-col">
          <Card className="search-result-card product-card">
            {/* Images Section (single image or carousel) */}
            {product.images && product.images.length > 0 ? (
              product.images.length === 1 ? (
                // Single Image Display without arrows
                <div className="image-container">
                  <Card.Img
                    variant="top"
                    src={product.images[0]}
                    alt={product[`name_${language}`] || 'Product Image'}
                    className="card-img-custom"
                    onError={(e) => {
                      // Initialize or increment the retry counter stored on the element
                      const retryCount = parseInt(e.target.getAttribute('data-retry') || '0', 10);
                      if (retryCount < 3) {
                        // Update the data attribute with the new retry count
                        e.target.setAttribute('data-retry', retryCount + 1);

                        // Try to reload the image by appending a timestamp to bust cache
                        setTimeout(() => {
                          e.target.src = product.images[0] + '?' + new Date().getTime();
                        }, 1000);
                      } else {
                        e.target.src = '/fallback-image.jpg'; 
                      }
                    }}
                    data-retry="0"
                  />
                </div>
              ) : (
                // Multiple Images Display with Carousel
                <Carousel
                  indicators={false}
                  controls
                  prevIcon={PrevIcon}
                  nextIcon={NextIcon}
                  interval={5000}
                  fade={false}
                >
                  {product.images.map((imgSrc, index) => (
                    <Carousel.Item key={index}>
                      <div className="image-container">
                        <img
                          className="d-block w-100 card-img-custom"
                          src={imgSrc}
                          alt={`Slide ${index + 1}`}
                          onError={(e) => {
                            // Initialize or increment the retry counter stored on the element
                            const retryCount = parseInt(e.target.getAttribute('data-retry') || '0', 10);
                            if (retryCount < 3) {
                              // Update the data attribute with the new retry count
                              e.target.setAttribute('data-retry', retryCount + 1);

                              // Try to reload the image by appending a timestamp to bust cache
                              setTimeout(() => {
                                e.target.src = imgSrc + '?' + new Date().getTime();
                              }, 1000);
                            } else {
                              e.target.src = '/fallback-image.jpg';
                            }
                          }}
                          data-retry="0"
                        />
                      </div>
                    </Carousel.Item>
                  ))}
                </Carousel>
              )
            ) : (
              // Placeholder if no images
              <div className="placeholder-image" />
            )}
            <Card.Body>
              <Card.Title>
                <div className="d-flex justify-content-between align-items-center">
                  <span>{product[`name_${language}`] || 'Unnamed Product'}</span>
                  {/* Updated: Only using Neon3DButton for products with a 3D model */}
                  { product.has3d && (
                    <div className="d-flex">
                      <Neon3DButton onClick={() => { window.location.href = `/3d?productId=${product.id}`; }} />
                    </div>
                  )}
                </div>
              </Card.Title>
              <Card.Text>
                {product[`category_${language}`] && (
                  <>
                    <strong>{translations.category}:</strong>{' '}
                    {product[`category_${language}`]}
                    <br />
                  </>
                )}
                {product[`sub_category_${language}`] && (
                  <>
                    <strong>{translations.subCategory}:</strong>{' '}
                    {product[`sub_category_${language}`]}
                    <br />
                  </>
                )}
                {product[`brand_${language}`] && (
                  <>
                    <strong>{translations.brand}:</strong>{' '}
                    {product[`brand_${language}`]}
                    <br />
                  </>
                )}
                {product[`material_${language}`] && (
                  <>
                    <strong>{translations.material}:</strong>{' '}
                    {product[`material_${language}`]}
                    <br />
                  </>
                )}
                {product.height && (
                  <>
                    <strong>Height:</strong> {product.height}
                    <br />
                  </>
                )}
                {product.width && (
                  <>
                    <strong>Width:</strong> {product.width}
                    <br />
                  </>
                )}
                {product.length && (
                  <>
                    <strong>Length:</strong> {product.length}
                    <br />
                  </>
                )}
                {product.diameter && (
                  <>
                    <strong>Diameter:</strong> {product.diameter}
                    <br />
                  </>
                )}
                {product[`bulb_type_${language}`] && (
                  <>
                    <strong>{translations.bulbType}:</strong>{' '}
                    {product[`bulb_type_${language}`]}
                    <br />
                  </>
                )}
                <strong>{translations.barcode}:</strong> {product.barcode}
                <CopyBarcode barcode={product.barcode} />
                <br />
              </Card.Text>
              <div className="d-flex justify-content-between">
                {product[`website_url_${language}`] && (
                  <a
                    href={product[`website_url_${language}`]}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn btn-primary"
                  >
                    {translations.viewOnWebsite}
                  </a>
                )}
                {product.images && product.images.length > 0 && (
                  <Button
                    variant="secondary"
                    onClick={() => handleSearchWithPicture(product.images[0])}
                  >
                    {translations.searchWithPicture}
                  </Button>
                )}
              </div>
            </Card.Body>
          </Card>
        </Col>
      ))}
      </Row>
    
      {/* Alert Dialog */}
      <AlertDialog
        show={alertState.show}
        onHide={hideAlert}
        title={alertState.title}
        message={alertState.message}
        variant={alertState.variant}
        icon={alertState.icon}
        autoClose={alertState.autoClose}
        autoCloseDelay={alertState.autoCloseDelay}
        showCloseButton={alertState.showCloseButton}
      />
    </>
  );
}

export default SearchResults;
